import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { 
  BarChart3,
  Download,
  Calendar,
  Route,
  Clock,
  Zap,
  TrendingUp,
  Users,
  Car,
  AlertTriangle,
  MapPin,
  Info,
  ArrowRight
} from 'lucide-react';
import { GpsLicenseGate } from '@/components/gps/GpsLicenseGate';
import { addDays } from 'date-fns';
import type { DateRange } from 'react-day-picker';

// Mock data for reports
const mockDistanceReport = [
  { vehicle: 'ABC-1234', distance_km: 1247.5, trips: 23, fuel_estimate: 156.8 },
  { vehicle: 'DEF-5678', distance_km: 892.3, trips: 18, fuel_estimate: 112.4 },
  { vehicle: 'GHI-9012', distance_km: 1534.7, trips: 31, fuel_estimate: 193.2 },
  { vehicle: 'JKL-3456', distance_km: 678.9, trips: 14, fuel_estimate: 85.6 }
];

const mockSpeedViolations = [
  { vehicle: 'ABC-1234', violations: 8, max_speed: 95, location: 'Marginal Pinheiros' },
  { vehicle: 'DEF-5678', violations: 3, max_speed: 87, location: 'Av. Paulista' },
  { vehicle: 'GHI-9012', violations: 12, max_speed: 102, location: 'Rodovia Anchieta' }
];

const mockIdleReport = [
  { vehicle: 'ABC-1234', idle_hours: 24.5, idle_percentage: 18.2, cost_estimate: 245.80 },
  { vehicle: 'DEF-5678', idle_hours: 16.3, idle_percentage: 12.1, cost_estimate: 163.40 },
  { vehicle: 'GHI-9012', idle_hours: 31.7, idle_percentage: 23.6, cost_estimate: 317.90 }
];

const mockDriverReport = [
  { driver: 'João Silva', distance_km: 2456.8, violations: 5, score: 87 },
  { driver: 'Maria Santos', distance_km: 1834.2, violations: 2, score: 94 },
  { driver: 'Pedro Costa', distance_km: 1567.3, violations: 8, score: 78 }
];

export default function GpsReports() {
  const { activeCompany } = useAuth();
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: addDays(new Date(), -30),
    to: new Date()
  });
  const [selectedVehicle, setSelectedVehicle] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [reportFormat, setReportFormat] = useState('pdf');

  const handleExport = (reportType: string) => {
    console.log(`Exporting ${reportType} report as ${reportFormat}`, {
      dateRange,
      selectedVehicle,
      selectedDriver
    });
    // Implement actual export logic here
  };

  return (
        <GpsLicenseGate>
          <Alert className="mb-6 bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-900">Esta funcionalidade foi movida</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span className="text-blue-800">
                Relatórios GPS agora fazem parte do <strong>FleetONE integrado</strong>.
                Acesse através do menu FleetONE → Relatórios para visualizar todos os relatórios unificados.
              </span>
              <Button variant="default" size="sm" asChild>
                <Link to={`/company/${activeCompany?.slug}/admin/systems/fleet/reports`}>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Ir para Relatórios FleetONE
                </Link>
              </Button>
            </AlertDescription>
          </Alert>

          <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-primary" />
              Relatórios GPS
            </h1>
            <p className="text-muted-foreground">
              Análises detalhadas de quilometragem, tempo e desempenho da frota
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Filtros de Relatório
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Período</Label>
                <Input
                  type="text"
                  placeholder="Últimos 30 dias"
                  value="Últimos 30 dias"
                  readOnly
                />
              </div>
              <div className="space-y-2">
                <Label>Veículo</Label>
                <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os veículos</SelectItem>
                    <SelectItem value="ABC-1234">ABC-1234</SelectItem>
                    <SelectItem value="DEF-5678">DEF-5678</SelectItem>
                    <SelectItem value="GHI-9012">GHI-9012</SelectItem>
                    <SelectItem value="JKL-3456">JKL-3456</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Motorista</Label>
                <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os motoristas</SelectItem>
                    <SelectItem value="joao">João Silva</SelectItem>
                    <SelectItem value="maria">Maria Santos</SelectItem>
                    <SelectItem value="pedro">Pedro Costa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Formato</Label>
                <Select value={reportFormat} onValueChange={setReportFormat}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="excel">Excel</SelectItem>
                    <SelectItem value="csv">CSV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reports Tabs */}
        <Tabs defaultValue="distance" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="distance" className="flex items-center gap-2">
              <Route className="h-4 w-4" />
              Quilometragem
            </TabsTrigger>
            <TabsTrigger value="violations" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Violações
            </TabsTrigger>
            <TabsTrigger value="idle" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Tempo Ocioso
            </TabsTrigger>
            <TabsTrigger value="drivers" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Motoristas
            </TabsTrigger>
          </TabsList>

          {/* Distance Report */}
          <TabsContent value="distance" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Relatório de Quilometragem</h3>
              <Button onClick={() => handleExport('distance')}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Total Percorrido</p>
                    <p className="text-2xl font-bold">4,353 km</p>
                    <div className="flex items-center gap-1 text-success text-sm">
                      <TrendingUp className="h-3 w-3" />
                      +12% vs mês anterior
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Total de Viagens</p>
                    <p className="text-2xl font-bold">86</p>
                    <div className="flex items-center gap-1 text-success text-sm">
                      <TrendingUp className="h-3 w-3" />
                      +8% vs mês anterior
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Consumo Estimado</p>
                    <p className="text-2xl font-bold">548L</p>
                    <div className="flex items-center gap-1 text-warning text-sm">
                      <TrendingUp className="h-3 w-3" />
                      +15% vs mês anterior
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr className="text-left">
                        <th className="p-4 font-medium">Veículo</th>
                        <th className="p-4 font-medium">Km Percorridos</th>
                        <th className="p-4 font-medium">Viagens</th>
                        <th className="p-4 font-medium">Consumo Estimado</th>
                        <th className="p-4 font-medium">Km/L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockDistanceReport.map((row, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-4 font-medium">{row.vehicle}</td>
                          <td className="p-4">{row.distance_km.toLocaleString()} km</td>
                          <td className="p-4">{row.trips}</td>
                          <td className="p-4">{row.fuel_estimate}L</td>
                          <td className="p-4">{(row.distance_km / row.fuel_estimate).toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Speed Violations Report */}
          <TabsContent value="violations" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Relatório de Violações de Velocidade</h3>
              <Button onClick={() => handleExport('violations')}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Total de Violações</p>
                    <p className="text-2xl font-bold text-destructive">23</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Velocidade Máxima</p>
                    <p className="text-2xl font-bold text-destructive">102 km/h</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Veículos Envolvidos</p>
                    <p className="text-2xl font-bold">3</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr className="text-left">
                        <th className="p-4 font-medium">Veículo</th>
                        <th className="p-4 font-medium">Violações</th>
                        <th className="p-4 font-medium">Velocidade Máxima</th>
                        <th className="p-4 font-medium">Local Principal</th>
                        <th className="p-4 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockSpeedViolations.map((row, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-4 font-medium">{row.vehicle}</td>
                          <td className="p-4">
                            <span className="bg-destructive/10 text-destructive px-2 py-1 rounded text-sm">
                              {row.violations}
                            </span>
                          </td>
                          <td className="p-4 font-medium text-destructive">{row.max_speed} km/h</td>
                          <td className="p-4">{row.location}</td>
                          <td className="p-4">
                            <span className="bg-warning/10 text-warning px-2 py-1 rounded text-sm">
                              Requer atenção
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Idle Time Report */}
          <TabsContent value="idle" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Relatório de Tempo Ocioso</h3>
              <Button onClick={() => handleExport('idle')}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Tempo Total Ocioso</p>
                    <p className="text-2xl font-bold text-warning">72.5h</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">% Média Ociosidade</p>
                    <p className="text-2xl font-bold text-warning">18.0%</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Custo Estimado</p>
                    <p className="text-2xl font-bold text-destructive">R$ 727</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr className="text-left">
                        <th className="p-4 font-medium">Veículo</th>
                        <th className="p-4 font-medium">Tempo Ocioso</th>
                        <th className="p-4 font-medium">% Ociosidade</th>
                        <th className="p-4 font-medium">Custo Estimado</th>
                        <th className="p-4 font-medium">Classificação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockIdleReport.map((row, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-4 font-medium">{row.vehicle}</td>
                          <td className="p-4">{row.idle_hours}h</td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-sm ${
                              row.idle_percentage > 20 ? 'bg-destructive/10 text-destructive' :
                              row.idle_percentage > 15 ? 'bg-warning/10 text-warning' :
                              'bg-success/10 text-success'
                            }`}>
                              {row.idle_percentage}%
                            </span>
                          </td>
                          <td className="p-4">R$ {row.cost_estimate.toFixed(2)}</td>
                          <td className="p-4">
                            {row.idle_percentage > 20 ? (
                              <span className="text-destructive">Alto</span>
                            ) : row.idle_percentage > 15 ? (
                              <span className="text-warning">Médio</span>
                            ) : (
                              <span className="text-success">Baixo</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Drivers Report */}
          <TabsContent value="drivers" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Relatório de Desempenho dos Motoristas</h3>
              <Button onClick={() => handleExport('drivers')}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr className="text-left">
                        <th className="p-4 font-medium">Motorista</th>
                        <th className="p-4 font-medium">Km Percorridos</th>
                        <th className="p-4 font-medium">Violações</th>
                        <th className="p-4 font-medium">Score</th>
                        <th className="p-4 font-medium">Desempenho</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockDriverReport.map((row, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-4 font-medium">{row.driver}</td>
                          <td className="p-4">{row.distance_km.toLocaleString()} km</td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-sm ${
                              row.violations > 5 ? 'bg-destructive/10 text-destructive' :
                              row.violations > 2 ? 'bg-warning/10 text-warning' :
                              'bg-success/10 text-success'
                            }`}>
                              {row.violations}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <div className="w-12 bg-muted rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full ${
                                    row.score >= 90 ? 'bg-success' :
                                    row.score >= 80 ? 'bg-warning' :
                                    'bg-destructive'
                                  }`}
                                  style={{ width: `${row.score}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium">{row.score}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            {row.score >= 90 ? (
                              <span className="text-success">Excelente</span>
                            ) : row.score >= 80 ? (
                              <span className="text-warning">Bom</span>
                            ) : (
                              <span className="text-destructive">Requer melhoria</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </GpsLicenseGate>
  );
}